import { computed, watch, ref, nextTick, toValue } from "vue";
import { useConversationStore } from "../stores";
import { conversationService } from "../services/conversation.service";
/**
 * Manages the active conversation: loads messages, sends, closes, and auto-scrolls.
 */
export function useConversation(options = {}) {
    const store = useConversationStore();
    const messagesEndRef = ref(null);
    function scrollToBottom() {
        nextTick(() => {
            messagesEndRef.value?.scrollIntoView({ behavior: "smooth" });
        });
    }
    // Load messages when conversationId is provided or changes
    watch(() => toValue(options.conversationId), async (id) => {
        if (!id)
            return;
        const conv = store.conversations.find((c) => c.id === id) ?? null;
        store.setActiveConversation(conv);
        await store.fetchMessages(id);
        scrollToBottom();
    }, { immediate: true });
    // Auto-scroll when messages are added
    watch(() => store.messages.length, () => scrollToBottom());
    return {
        conversation: computed(() => store.activeConversation),
        messages: computed(() => store.messages),
        isLoading: computed(() => store.isLoadingMessages),
        isSubmitting: computed(() => store.isSubmitting),
        isClosed: computed(() => store.activeConversation
            ? !conversationService.isActive(store.activeConversation)
            : false),
        messagesEndRef,
        scrollToBottom,
        async sendMessage(payload) {
            if (!store.activeConversation)
                return;
            await store.sendMessage(store.activeConversation.id, payload);
            scrollToBottom();
        },
        async closeConversation() {
            if (!store.activeConversation)
                return;
            await store.closeConversation(store.activeConversation.id);
        },
        async editMessage(messageId, text) {
            await store.editMessage(messageId, text);
        },
        async deleteMessage(messageId) {
            await store.deleteMessage(messageId);
        },
    };
}
